import { type FC, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { CardGridArea } from "widgets";

import { renderSequenceAttributesWidgets } from "@/app-utils/attributes-widgets.tsx";
import { useLoadDispatchWithIdAsNumber } from "@/app-utils/hooks/useLoadDispatchWithIdAsNumber.ts";
import { type User } from "@/graphql.ts";
import { type ISequenceState, shouldLoadSequence } from "@/redux/modules/sequence.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { ENTITY_TYPE } from "@/settings/settings.js";

import { createSummaryCardsFromClassifications } from "../../app-utils/card.js";
import { AttributesListWidget } from "../core/navigation-panel/AttributeWidget.js";
import { type IPageHeaderProps } from "../core/navigation-panel/PageHeader.js";
import { DatabaseCard } from "./cards/DatabaseCard.js";
import { GenomeCard } from "./cards/GenomeCard.js";
import { IntReferencesCard } from "./cards/IntReferencesCard.js";
import { ProteinListCard } from "./cards/ProteinListCard.js";
import { RelatedProjectsCard } from "./cards/RelatedProjectsCard.js";
import { SequenceCard } from "./cards/SequenceCard.js";
import { TranscriptListCard } from "./cards/TranscriptListCard.js";
import GenericEntitySummaryView from "./GenericEntitySummaryView.js";

interface ISequenceSummaryPageOwnProps {
  sequenceId: number; // In case the path is not "/sequence/:sequenceId" @see BlastResultHitSummaryView
  title?: string;
  pageHeaderProps?: IPageHeaderProps;
  entityType?: string;
}

const SequenceSummaryView: FC<ISequenceSummaryPageOwnProps> = (props) => {
  const { sequenceId } = useParams<"sequenceId">();
  const { sequence, isLoading } = useSelector<IAppState, ISequenceState>((state) => state.sequence);
  const currentUser = useSelector<IAppState, User>((state) => state.auth.session);
  const load = useLoadDispatchWithIdAsNumber(shouldLoadSequence, props.sequenceId ?? sequenceId);

  useEffect(() => {
    void load();
  }, [load]);

  const showSpinner = isLoading || !sequence;

  return (
    <GenericEntitySummaryView
      title={(props.title ? `${props.title}: ` : "") + (sequence ? sequence?.sequenceId : "")}
      subtitle={sequence?.description ?? "Sequence Summary"}
      headerAttributes={[
        <AttributesListWidget
          attributes={renderSequenceAttributesWidgets(sequence)}
          key={"sequence-summary-view-widget-1"}
        />,
      ]}
      headerTags={props.pageHeaderProps?.tags}
      project={props.pageHeaderProps?.project}
      entity={{ id: sequenceId ?? props.sequenceId, groupType: props.entityType || ENTITY_TYPE.SEQUENCE }}
      isLoading={showSpinner}
      getEntityName={() => sequence?.sequenceId}
    >
      {sequence && (
        <>
          <SequenceCard sequence={sequence} area={CardGridArea.Left} />
          <GenomeCard genome={sequence.organism} area={CardGridArea.Left} />
          <DatabaseCard genome={sequence.organism} database={sequence.database} area={CardGridArea.Left} />
          {createSummaryCardsFromClassifications(sequence.allClassifications, CardGridArea.Left)}
          <TranscriptListCard
            transcripts={sequence.transcripts}
            ignoreIfEmpty={true}
            title="Probe-related Transcripts"
            showSource={true}
            area={CardGridArea.Right}
          />
          <ProteinListCard
            proteins={sequence.proteins}
            ignoreIfEmpty={true}
            title="Probe-related Proteins"
            showSource={true}
            area={CardGridArea.Right}
          />
          <IntReferencesCard references={sequence.internalReferences} area={CardGridArea.Right} />
          <RelatedProjectsCard
            relatedProjects={sequence.relatedProjects}
            currentUser={currentUser}
            area={CardGridArea.Panel}
          />
        </>
      )}
    </GenericEntitySummaryView>
  );
};

export default SequenceSummaryView;
